import {
    AfterViewInit,
    Component,
    EventEmitter, Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {WizardModalComponent} from '@app/shared/components';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Ticket} from '@models/ticket.model';
import {OperationType} from '@app/tool/model/operationType.model';
import {OperationTypeService} from '@services/operation-type.service';
import {Log} from "ng2-logger";
import {Tool} from '@app/tool/model/tool.model';
import {ToolReturn} from '@app/shared/models/tool-return.model';
import {AppUtils} from '@app/shared/util/app.utils';
import {ToolSet} from '@app/tool/model/toolSet.model';
import {TableConfig} from "@app/shared/util/table-config.utils";
import {TicketType} from "@models/enums/ticket-type.enum";
import {TreeModel} from "@components/tree/tree.model";
import {TicketStatus} from "@models/enums/ticket-status";
import {TranslateService} from "@ngx-translate/core";
import {MaintenancePlanUtils} from '@app/shared/util/maintenance-plan.utils';
import {ToolUsageService} from '@app/core/services/tool-usage.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MaintenancePlansService} from '@app/maintenance/service/maintenance-plans.service';
import {RevisionInfo} from "@components/revision-info/models/revision-info.model";
import {RevisionInfoModalComponent} from "@components/revision-info-modal/revision-info-modal.component";


@Component({
    selector: 'app-maintenace-ticket-modal',
    templateUrl: './maintenace-ticket-modal.component.html',
    styleUrls: ['./maintenace-ticket-modal.component.scss']
})
export class MaintenaceTicketModalComponent implements OnInit, AfterViewInit {
    _logger = Log.create(MaintenaceTicketModalComponent.name);

    @ViewChild(WizardModalComponent, {static: false}) modal: WizardModalComponent;
    @ViewChild(RevisionInfoModalComponent, {static: false}) revisionInfoModal: RevisionInfoModalComponent;

    @Input() config: TableConfig;
    @Input() isLoading: boolean;
    @Input() similarTickets: TreeModel[] = [];

    @Output('create') create: EventEmitter<any> = new EventEmitter();
    @Output() ticketCheck: EventEmitter<any> = new EventEmitter();


    public ticket: Ticket = new Ticket();

    selectToolsForm: UntypedFormGroup;
    operationsForm: UntypedFormGroup;
    operationsWithRecurrencesForm: UntypedFormGroup;

    error = false;
    showValidations = false;
    selectedTools: Tool[] = [];
    selectedRecurrencesItems: any = [];
    requestId: number;
    toolReturn: ToolReturn;
    lineId: number;
    initialOperations: OperationType[] = [];
    operationTypes: OperationType[] = [];

    operationsWithRecurrences = [];
    maintenancePlanItems = [];
    selectedOperations = [];
    mandatoryMaintenancePlanItems = [];

    groupedTools = [];

    ticketOperations = [];
    overviewItems = [];
    firstTool: any;
    revisionInfo: RevisionInfo[] = [];

    maintenanceToolsAtLine: Tool [];

    constructor(private operationTypeService: OperationTypeService,
                private translateService: TranslateService,
                private toolUsageService: ToolUsageService,
                private router: Router,
                private route: ActivatedRoute,
                private maintenancePlanService: MaintenancePlansService) {
    }

    ngOnInit() {
        this.onFormInit();
        this.getOperationTypes();
    }

    ngAfterViewInit() {
    }

    open(toolReturn: ToolReturn, maintenanceToolsAtLine: Tool [], operations: OperationType[] = []) {
        if (maintenanceToolsAtLine) {
            this.maintenanceToolsAtLine = maintenanceToolsAtLine;

            this.firstTool = maintenanceToolsAtLine[0] instanceof ToolSet ?
                (maintenanceToolsAtLine[0] as ToolSet).tools[0]
                : maintenanceToolsAtLine[0];
        }

        this.initialOperations = operations;
        this.toolReturn = toolReturn;
        this.requestId = toolReturn.toolRequest.id;
        this.lineId = toolReturn.line.id;

        this.onFormInit();

        this.selectedRecurrencesItems = [];
        this.selectedTools = [];
        this.maintenancePlanItems = [];
        this.operationsWithRecurrences = [];
        this.selectedOperations = [];

        this.ticketOperations = [];

        this.modal.open();
    }

    onFormInit() {
        const ticketId = 0;
        const ticketDescription = this.toolReturn ? this.toolReturn.reason : '-';
        const ticketOperationTypes = this.initialOperations;
        const selectedTools = [];

        if (!this.selectToolsForm) {
            this.selectToolsForm = new UntypedFormGroup({
                'id': new UntypedFormControl(ticketId),
                'selectedTools': new UntypedFormControl(selectedTools, [AppUtils.atLeastOneToolValidator(1)])
            });
        }

        this.operationsForm = new UntypedFormGroup({
            'description': new UntypedFormControl(ticketDescription, [Validators.required]),
            'operationTypes': new UntypedFormControl(ticketOperationTypes, [AppUtils.notEmptyValidation]),
        });

        this.operationsWithRecurrencesForm = new UntypedFormGroup({
            'selectedRecurrencesItems': new UntypedFormControl([], [AppUtils.atLeastOneToolValidator(1)])
        });

        this._logger.info('onFormInit:: form initialised', this.selectToolsForm, this.operationsForm);
    }

    private getOperationTypes() {
        // Observable for retrieving operation types data
        this.operationTypeService.getAllOperationTypes().subscribe(
            (data: any) => {
                this.operationTypes = data.operationTypes;
            },
            (error: any) => {
                this._logger.error("Error getting operation types", error);
            }
        );
    }

    onStepChange(step) {
        this._logger.info('onStepChange:: ', step);

        if (!this.toolsHaveMaintenancePlan() && step === 2) {
            step = 3;
        }

        if (step === 1) {
            if (this.maintenanceToolsAtLine && this.maintenanceToolsAtLine.length > 0) {
                this.selectToolsForm.controls.selectedTools.setValue(this.mapToolsToList(this.maintenanceToolsAtLine))
            }

            if (this.selectToolsForm.controls.selectedTools.value) {
                const toolsIds = this.selectToolsForm.controls.selectedTools.value.map(x => x.data.id);
                const tableConfig = new TableConfig();
                toolsIds.forEach(element => {
                    tableConfig.filters.push({field: 'toolIds', value: element})
                });

                this.isLoading = true;

                this.maintenancePlanService.getMandatoryPlanItemsByToolIds(toolsIds).subscribe((data: any) => {
                    this.mandatoryMaintenancePlanItems = data;
                    if (this.mandatoryMaintenancePlanItems && this.mandatoryMaintenancePlanItems.length) {
                        this.operationsForm.get('operationTypes').clearValidators();
                        this.operationsForm.get('operationTypes').updateValueAndValidity();
                    }
                    this.isLoading = false;
                });

                this.toolUsageService.getProductionData(toolsIds).subscribe((data: RevisionInfo[]) => {
                    this.revisionInfo = data;
                    this.isLoading = false;
                });
            }
        }
        if (step === 2) {
            this.selectedOperations = this.operationsForm.controls.operationTypes.value.map(x => x.id);
            const toolIds = this.selectToolsForm.controls.selectedTools.value.map(x => x.data.id);

            this.isLoading = true;
            this.maintenancePlanService.getPlanItemsByToolIds(toolIds).subscribe((data: any) => {
                this.maintenancePlanItems = data;

                const allOperationIds = this.mandatoryMaintenancePlanItems
                    ? this.selectedOperations.concat(this.mandatoryMaintenancePlanItems.map(item => item.operationId))
                    : this.selectedOperations;

                // Transform selected operations in operations with recurrences
                this.operationsWithRecurrences = MaintenancePlanUtils.groupMaintenancePlanItems(this.maintenancePlanItems,
                    this.mandatoryMaintenancePlanItems,
                    allOperationIds,
                    this.translateService.instant('admin.toolMaintenancePlans.recurrences.recurringEvery'));

                // Initialed the operationsWithRecurrencesForm with preselected recurrences
                this.operationsWithRecurrencesForm.setValue({
                    'selectedRecurrencesItems': MaintenancePlanUtils.groupSelectedOperations(this.operationsWithRecurrences)
                });

                this.isLoading = false;
            });
        }
        if (step === 3) {
            this.ticketCheck.emit({
                description: this.operationsForm.controls.description.value,
                operationTypes: this.operationsForm.controls.operationTypes.value,
                selectedTools: this.selectToolsForm.controls.selectedTools.value
            });

            if (this.toolsHaveMaintenancePlan()) {
                this.groupedTools = MaintenancePlanUtils.groupTools(this.selectToolsForm.controls.selectedTools.value);
                const operationsWithSelectedRecurrences = this.operationsWithRecurrencesForm.controls.selectedRecurrencesItems.value;
                this.ticketOperations = MaintenancePlanUtils.computeTicketOperations(this.groupedTools,
                    operationsWithSelectedRecurrences,
                    this.maintenancePlanItems)

                this.overviewItems = MaintenancePlanUtils.buildOverviewItems(this.groupedTools,
                    operationsWithSelectedRecurrences,
                    this.selectToolsForm.controls.selectedTools.value,
                    this.maintenancePlanItems,
                    this.translateService.instant(
                        'admin.toolMaintenancePlans.recurrences.recurringEvery'
                    ));
            }
        } else {
            this.similarTickets = [];
        }
    }

    canContinueStep1() {
        return this.operationsForm.valid
            || (this.mandatoryMaintenancePlanItems.length > 0 && this.operationsForm.get('description').valid);
    }

    cancel() {
        this.resetData();
        this.modal.close();
    }

    onStepErrosChange() {
        this.showValidations = !this.operationsForm.valid;
    }

    isOpen() {
        return this.modal?.isOpen;
    }

    resetData() {
        this.selectToolsForm.reset();
        this.operationsForm.reset();
        this.operationsWithRecurrencesForm.reset();
        this._logger.info('resetForm:: Resetting form');
        this.showValidations = false;
        this.modal.reset();

        this.selectedTools = [];
        this.selectedRecurrencesItems = [];
        this.ticketOperations = [];
        this.maintenancePlanItems = [];

        this.firstTool = null;
        this.config = new TableConfig();
    }

    createTicket() {
        let ticketAtLine: any = {};
        let ticketAtService: any = {};

        if (this.isTicketWithMaintenanceAtLine()) {
            ticketAtLine = this.computeTicket();
            ticketAtLine.selectedTools = this.selectToolsForm.controls.selectedTools.value;
            ticketAtLine.hasMaintenanceAtLine = true;
            ticketAtLine.ticketOperations = this.ticketOperations;
            ticketAtLine.lineId = this.lineId;
        } else {
            ticketAtService = this.computeTicket();
            ticketAtService.selectedTools = this.selectToolsForm.controls.selectedTools.value;
            ticketAtService.hasMaintenanceAtLine = false;
            ticketAtService.ticketOperations = this.ticketOperations;
        }

        this.create.emit({TicketAtService: ticketAtService, TicketAtLine: ticketAtLine});
        this.cancel();
    }

    hasSimilarTicketsInProgress() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        return similarTicketsInProgress && similarTicketsInProgress.length > 0;
    }

    computeSimilarTickets() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        if (similarTicketsInProgress && similarTicketsInProgress.length) {
            return similarTicketsInProgress;
        }

        return this.similarTicketsNew();
    }

    computeOverviewStepDescription() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        if (similarTicketsInProgress && similarTicketsInProgress.length) {
            return this.translateService.instant('admin.tickets.create-modal.overview.error-message');
        }

        const similarTicketsNew = this.similarTicketsNew();
        if (similarTicketsNew && similarTicketsNew.length) {
            return this.translateService.instant('admin.tickets.create-modal.overview.modal-description');
        }

        return '';
    }

    goToPlan(planId: number) {
        const url = this.router.serializeUrl(
            this.router.createUrlTree(
                [`/read-tool-maintenance-plans/${planId}/details/`],
                {relativeTo: this.route}
            )
        );
        localStorage.setItem('openedInNewTab', 'true');
        window.open(url);
    }

    onToolScan($event) {
        if (this.firstTool) {
            return;
        }

        this.firstTool = $event.data;
        const needsMaintenancePlan = this.firstTool && this.firstTool.maintenancePlanId != null;

        this.config.filters.push({field: 'needsMaintenancePlan', value: needsMaintenancePlan});
    }

    getInfoMessage() {
        if (!this.firstTool) {
            return '';
        }

        if (this.firstTool.maintenancePlanId != null) {
            return this.translateService.instant('admin.tickets.create-modal.info.toolsWithMaintenancePlan');
        } else {
            return this.translateService.instant('admin.tickets.create-modal.info.toolsWithoutMaintenancePlan');
        }
    }

    toolsHaveMaintenancePlan() {
        return this.firstTool && this.firstTool.maintenancePlanId != null;
    }

    openRevisionModal() {
        this.revisionInfoModal.open(this.revisionInfo);
    }

    isTicketWithMaintenanceAtLine() {
        return this.maintenanceToolsAtLine && this.maintenanceToolsAtLine.length > 0;
    }

    private computeTicket(): any {
        return {
            description: this.operationsForm.controls.description.value,
            operationTypes: MaintenancePlanUtils.computeOperationTypes(this.operationsForm.controls.operationTypes.value,
                this.mandatoryMaintenancePlanItems,
                this.operationTypes),
            hasPriority: true,
            type: TicketType.Maintenance,
            lineToolRequestId: this.requestId,
        };
    }

    private mapToolsToList(tools: any[]): any[] {
        const result = [];
        tools.forEach(tool => {
            if (tool instanceof Tool) {
                result.push(tool);
            } else if (tool instanceof ToolSet) {
                tool.tools.forEach(toolInSet => {
                    result.push(toolInSet);
                });
            }
        });
        return result.map(t => ({data: t}));
    }

    private similarTicketsInProgress() {
        return this.similarTickets ? this.similarTickets.filter(y => y.data.ticketStatus === TicketStatus.READY
            || y.data.ticketStatus === TicketStatus.WAITING_FOR_TOOLS
            || y.data.ticketStatus === TicketStatus.IN_PROGRESS) : []
    }

    private similarTicketsNew() {
        return this.similarTickets ? this.similarTickets.filter(y => y.data.ticketStatus === TicketStatus.UNAVAILABLE
            || y.data.ticketStatus === TicketStatus.NEW) : [];
    }
}
